import React, { useEffect, useState, useMemo } from 'react';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CookiesProvider } from 'react-cookie';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useCookies } from 'react-cookie';



import './App.css';
import LoginPage from './pages/login'
import HomePage from "./pages/homepage";
import { AddInventory, ViewInventory, ViewInventoryDetails, AddPreparation, AddWastage } from "./pages/inventory";
import { AddBill, ViewBillsList, ViewBillDetails } from './pages/Bills';
import PaperWrapper from "./components/page_wrapper";
import { ViewOrders } from './pages/Orders';
import AnalyticsDashboard from './pages/analytics';



/**
 * Shared theme options.
 *
 * This app is used mostly on a phone, standing up, often one-handed in a
 * kitchen - so the defaults below raise every interactive element to a
 * comfortable tap size and stop small text getting smaller on small screens.
 * Setting it here fixes it everywhere at once rather than per component.
 */
const TOUCH_TARGET = 44;

const sharedOptions = {
  shape: { borderRadius: 10 },
  typography: {
    // MUI's default caption (0.75rem = 12px) is the floor, not a starting point.
    caption: { fontSize: '0.78rem' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { minHeight: TOUCH_TARGET, paddingLeft: 14, paddingRight: 14 },
        // `size="small"` is used for secondary actions; keep it visually
        // smaller but never below a tappable height.
        sizeSmall: { minHeight: TOUCH_TARGET, paddingTop: 4, paddingBottom: 4 },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET } },
    },
    MuiToggleButton: {
      styleOverrides: { root: { minHeight: TOUCH_TARGET } },
    },
    MuiSelect: {
      styleOverrides: { select: { minHeight: `${TOUCH_TARGET - 8}px`, display: 'flex', alignItems: 'center' } },
    },
    MuiOutlinedInput: {
      styleOverrides: { input: { minHeight: `${TOUCH_TARGET - 20}px` } },
    },
    MuiTab: {
      styleOverrides: { root: { minHeight: TOUCH_TARGET, textTransform: 'none' } },
    },
    // Tables are the one place horizontal scrolling is correct rather than a
    // bug: the alternative is truncating figures.
    MuiTableContainer: {
      styleOverrides: { root: { maxWidth: '100%', overflowX: 'auto' } },
    },
    // Deliberately NOT `whiteSpace: nowrap` on cells. It keeps numbers tidy but
    // stops long text wrapping, which pushed tables past the card that clips
    // them - a cut-off value is worse than a wrapped one. Numeric columns opt
    // in individually where alignment matters.
    MuiTable: {
      styleOverrides: { root: { tableLayout: 'auto' } },
    },
  },
};

const darkTheme = createTheme({
  ...sharedOptions,
  palette: { mode: 'dark' },
});

const lightTheme = createTheme({
  ...sharedOptions,
  palette: { mode: 'light' },
});


function App() {
  const [showSnackBar, setShowSnackBar] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("success");
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [cookies, setCookies] = useCookies(['divash-admin-theme']);

  useEffect(() => {
    if (!cookies['divash-admin-theme']) {
      setCookies('divash-admin-theme', "light", { path: '/' });
    }
  }, [cookies, setCookies]);

  const theme = useMemo(
    () => cookies['divash-admin-theme'] === 'light' ? lightTheme : darkTheme,
    [cookies['divash-admin-theme']]
  );

  const showErrorMessage = (message) => {
    setSnackBarMessage(message);
    setAlertSeverity("error");
    setShowSnackBar(true);
  };

  const showSuccessMessage = (message) => {
    setSnackBarMessage(message);
    setAlertSeverity("success");
    setShowSnackBar(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setShowSnackBar(false);
  };

  return (
    <ThemeProvider theme={theme} >
      <CssBaseline />
      <BrowserRouter>
        <PaperWrapper theme={cookies['divash-admin-theme']} changeTheme={(theme) => {
          setCookies('divash-admin-theme', theme);
        }}>
          <Snackbar
            open={showSnackBar}
            autoHideDuration={30000}
            onClose={handleSnackbarClose}
          >
            <Alert
              onClose={handleSnackbarClose}
              variant="filled"
              severity={alertSeverity}
            >
              {snackBarMessage}
            </Alert>
          </Snackbar>
          <CookiesProvider defaultSetOptions={{ path: '/' }}>
            <Routes>
              <Route path="/" element={<LoginPage showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              {/* Removed redundant index route */}
              <Route path="/home" element={<HomePage showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/add/status" element={<AddInventory showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/add/preparation" element={<AddPreparation showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/view" element={<ViewInventory showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/view/details/*" element={<ViewInventoryDetails showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/add/wastage/" element={<AddWastage route="wastage" showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/inventory/add/usage/" element={<AddWastage route="usage" showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/bill/add" element={<AddBill showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/bill/view" element={<ViewBillsList showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/bill/view/*" element={<ViewBillDetails showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/orders/view" element={<ViewOrders showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route path="/analytics" element={<AnalyticsDashboard showErrorMessage={showErrorMessage} showSuccessMessage={showSuccessMessage} />} />
              <Route
                path="*"
                element={
                  <>TODO</>
                }
              />
            </Routes>
          </CookiesProvider>
        </PaperWrapper>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
